import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClientService } from "@/services/ClientService";
import { useToast } from "@/hooks/use-toast";
import { ClientContact } from "@shared/schema";
import { Loader2 } from "lucide-react";

const contactSchema = z.object({
    name: z.string().min(1, "Name is required"),
    email: z.string().email("Invalid email").optional().or(z.literal("")),
    phone: z.string().optional(),
    role: z.string().optional(),
    isPrimary: z.boolean().default(false),
});

type ContactFormValues = z.infer<typeof contactSchema>;

interface ContactDialogProps {
    isOpen: boolean;
    onClose: () => void;
    contact?: ClientContact;
    clientId: number;
}

export function ContactDialog({ isOpen, onClose, contact, clientId }: ContactDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const isEditing = !!contact;

    const form = useForm<ContactFormValues>({
        resolver: zodResolver(contactSchema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            role: "",
            isPrimary: false,
        },
    });

    useEffect(() => {
        if (contact) {
            form.reset({
                name: contact.name,
                email: contact.email || "",
                phone: contact.phone || "",
                role: contact.role || "",
                isPrimary: contact.isPrimary,
            });
        } else {
            form.reset({
                name: "",
                email: "",
                phone: "",
                role: "",
                isPrimary: false,
            });
        }
    }, [contact, form, isOpen]);

    const mutation = useMutation({
        mutationFn: async (values: ContactFormValues) => {
            if (isEditing && contact) {
                return ClientService.updateContact(contact.id, values);
            } else {
                return ClientService.createContact(clientId, values);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["client-contacts", clientId] });
            toast({
                title: isEditing ? "Contact updated" : "Contact created",
                description: `Successfully ${isEditing ? "updated" : "created"} ${form.getValues("name")}`,
            });
            onClose();
        },
        onError: (error: any) => {
            toast({
                title: "Error",
                description: error.message || "Something went wrong",
                variant: "destructive",
            });
        },
    });

    const onSubmit = (values: ContactFormValues) => {
        mutation.mutate(values);
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isEditing ? "Edit Contact" : "Add New Contact"}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="John Doe" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Email</FormLabel>
                                    <FormControl>
                                        <Input type="email" placeholder="john@example.com" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="phone"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Phone</FormLabel>
                                    <FormControl>
                                        <Input placeholder="+353 ..." {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="role"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Role</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Media Planner" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="isPrimary"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel>Primary Contact</FormLabel>
                                        <p className="text-sm text-muted-foreground">
                                            This person is the main contact for this client.
                                        </p>
                                    </div>
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={mutation.isPending}>
                                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {isEditing ? "Save Changes" : "Add Contact"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
